"use client";

import { useEffect, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLanguage } from "@/contexts/LanguageContext";

type MobileEndDrawerProps = {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
};

/** Full-height panel from the trailing screen edge; `lg:hidden`. LTR: physical right; RTL: physical left. */
export function MobileEndDrawer({
  open,
  title,
  closeLabel,
  onClose,
  children,
}: MobileEndDrawerProps) {
  const titleId = useId();
  const { isRtl } = useLanguage();

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] lg:hidden">
      <button
        type="button"
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        className={`absolute top-0 flex h-full w-[min(100%,22rem)] max-w-[100vw] flex-col border-[#E5E5E5] bg-white shadow-xl ${
          isRtl
            ? "left-0 border-e"
            : "right-0 border-s"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#E5E5E5] px-4 py-3">
          <h2
            id={titleId}
            className="text-base font-semibold text-[#130F0E]"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F4F4F4]"
            aria-label={closeLabel}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] scrollbar-hide">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
